import React, { useState, useEffect, useRef } from 'react';
import { Settings, Save, RotateCcw, Clock, X, AlertCircle, CheckCircle, Lock, User, Building2, ChevronDown, ChevronUp, Info, Loader } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

/**
 * My Settings - Role-Based Settings Customization
 *
 * PURPOSE: Allow users to customize settings within their permission tier
 *
 * FEATURES:
 * - View effective settings with inheritance visualization
 * - Customize allowed settings based on role
 * - See what settings are controlled by organization
 * - View change history
 * - Reset to organization defaults
 *
 * INHERITANCE: System Defaults → Organization → Personal Overrides
 *
 * PERMISSIONS:
 * - Salesperson: Tier 4 only (Route Optimization)
 * - Area Manager: Tier 3 + 4 (Distance & Route)
 * - HQ Manager: Tier 2 + 3 + 4 (Scoring, Distance & Route)
 */

function useDraggable(enabled) {
    const [pos, setPos] = useState(null);
    const dragging = useRef(false);
    const offset   = useRef({ x: 0, y: 0 });
    const onMouseDown = (e) => {
        if (!enabled) return;
        if (e.target.closest('button')) return;
        dragging.current = true;
        const rect = e.currentTarget.closest('[data-draggable]').getBoundingClientRect();
        offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        document.body.style.userSelect = 'none';
        const onMove = (ev) => { if (dragging.current) setPos({ x: ev.clientX - offset.current.x, y: ev.clientY - offset.current.y }); };
        const onUp   = () => { dragging.current = false; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };
    return { pos, headerProps: { onMouseDown } };
}

const MySettings = ({ currentUser, onClose }) => {
    const [effectiveSettings, setEffectiveSettings] = useState(null);
    const [myOverrides, setMyOverrides] = useState({});
    const [allowedSettings, setAllowedSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [changeNotes, setChangeNotes] = useState('');
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        tier2: false,
        tier3: true,
        tier4: true
    });
    const [pendingChanges, setPendingChanges] = useState({});

    useEffect(() => {
        loadMySettings();
        loadAllowedOverrides();
    }, []);

    const loadMySettings = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${API_BASE_URL}/api/role-settings/my-settings`);
            setEffectiveSettings(response.data.effective_settings);
            setMyOverrides(response.data.my_overrides);
            setAllowedSettings(response.data.allowed_overrides);
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load settings');
            setLoading(false);
        }
    };

    const loadAllowedOverrides = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/role-settings/allowed-overrides`);
            setAllowedSettings(response.data.allowed_settings);
        } catch (err) {
            console.error('Failed to load allowed overrides:', err);
        }
    };

    const loadHistory = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/role-settings/history?limit=10`);
            setHistory(response.data);
            setShowHistory(true);
        } catch (err) {
            setError('Failed to load history');
        }
    };

    const handleChange = (field, value) => {
        setPendingChanges(prev => ({ ...prev, [field]: value }));
        setSuccess(null);
    };

    const handleSave = async () => {
        if (Object.keys(pendingChanges).length === 0) { setError('No changes to save'); return; }
        if (!changeNotes.trim()) { setError('Please provide a reason for these changes'); return; }
        try {
            setSaving(true);
            setError(null);
            await axios.put(`${API_BASE_URL}/api/role-settings/my-overrides`, {
                settings: pendingChanges,
                change_notes: changeNotes
            });
            setSuccess('Settings saved successfully!');
            setSaving(false);
            setChangeNotes('');
            setPendingChanges({});
            await loadMySettings();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save settings');
            setSaving(false);
        }
    };

    const handleReset = async () => {
        try {
            setSaving(true);
            setError(null);
            await axios.delete(`${API_BASE_URL}/api/role-settings/my-overrides`);
            setSuccess('Settings reset to organization defaults!');
            setSaving(false);
            setShowResetConfirm(false);
            setPendingChanges({});
            await loadMySettings();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to reset settings');
            setSaving(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const getEffectiveValue = (field) => {
        if (pendingChanges.hasOwnProperty(field)) return pendingChanges[field];
        return effectiveSettings?.[field];
    };

    const isOverridden = (field) => myOverrides.hasOwnProperty(field);
    const isPending = (field) => pendingChanges.hasOwnProperty(field);
    const canCustomize = (field) => allowedSettings.includes(field);
    const hasChanges = Object.keys(pendingChanges).length > 0;

    const settingsByTier = {
        tier1: {
            title: 'Critical Business Rules',
            description: 'Controlled by your organization admin',
            icon: Lock,
            badgeClass: 'bg-red-900/30 border-red-700 text-red-300',
            settings: [
                { key: 'recent_contact_exclusion_days', label: 'Recent Contact Exclusion', unit: 'days', description: 'AI won\'t recommend leads contacted within this many days' },
                { key: 'visit_duplicate_prevention_days', label: 'Visit Duplicate Prevention', unit: 'days', description: 'Prevent duplicate visits within this period' },
                { key: 'sales_cycle_length_days', label: 'Sales Cycle Length', unit: 'days', description: 'Typical sales cycle duration' },
            ]
        },
        tier2: {
            title: 'Scoring & Prioritization',
            description: 'HQ Managers can customize these',
            icon: Building2,
            badgeClass: 'bg-orange-900/30 border-orange-700 text-orange-300',
            settings: [
                { key: 'weight_priority', label: 'Priority Weight', unit: '', description: 'How much priority affects scoring (0-1)', min: 0, max: 1, step: 0.01 },
                { key: 'weight_revenue', label: 'Revenue Weight', unit: '', description: 'How much revenue potential affects scoring (0-1)', min: 0, max: 1, step: 0.01 },
                { key: 'weight_proximity', label: 'Proximity Weight', unit: '', description: 'How much distance affects scoring (0-1)', min: 0, max: 1, step: 0.01 },
                { key: 'weight_opportunity', label: 'Opportunity Weight', unit: '', description: 'How much opportunity affects scoring (0-1)', min: 0, max: 1, step: 0.01 },
                { key: 'high_revenue_threshold', label: 'High Revenue Threshold', unit: '¥', description: 'Revenue above this is considered high value' },
            ]
        },
        tier3: {
            title: 'Distance & Geography',
            description: 'Area Managers can customize these',
            icon: Building2,
            badgeClass: 'bg-blue-900/30 border-blue-700 text-blue-300',
            settings: [
                { key: 'immediate_distance_max', label: 'Immediate Distance Max', unit: 'km', description: 'Maximum distance for "immediate" category', step: 0.5 },
                { key: 'near_distance_max', label: 'Near Distance Max', unit: 'km', description: 'Maximum distance for "near" category', step: 0.5 },
                { key: 'far_distance_max', label: 'Far Distance Max', unit: 'km', description: 'Maximum distance for "far" category', step: 0.5 },
                { key: 'very_close_threshold_km', label: 'Very Close Threshold', unit: 'km', description: 'Distance considered very close', step: 0.5 },
            ]
        },
        tier4: {
            title: 'Route Optimization',
            description: 'You can customize these settings',
            icon: User,
            badgeClass: 'bg-green-900/30 border-green-700 text-green-300',
            settings: [
                { key: 'morning_route_max_distance_km', label: 'Morning Route Max Distance', unit: 'km', description: 'Maximum distance for morning routes', step: 0.5 },
                { key: 'average_city_speed_kmh', label: 'Average City Speed', unit: 'km/h', description: 'Typical driving speed in your territory' },
                { key: 'light_traffic_delay_factor', label: 'Light Traffic Delay', unit: 'x', description: 'Time multiplier for light traffic', min: 1.0, max: 2.0, step: 0.05 },
                { key: 'moderate_traffic_delay_factor', label: 'Moderate Traffic Delay', unit: 'x', description: 'Time multiplier for moderate traffic', min: 1.0, max: 2.0, step: 0.05 },
                { key: 'heavy_traffic_delay_factor', label: 'Heavy Traffic Delay', unit: 'x', description: 'Time multiplier for heavy traffic', min: 1.0, max: 2.0, step: 0.05 },
            ]
        }
    };

    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
    const { pos, headerProps } = useDraggable(isDesktop);
    const panelStyle = pos && isDesktop ? { position: 'fixed', left: pos.x, top: pos.y, margin: 0, transform: 'none' } : {};

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div data-draggable style={panelStyle} className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div {...headerProps} className={`bg-gradient-to-r from-blue-900 to-purple-900 px-6 py-4 flex items-center justify-between flex-shrink-0 ${isDesktop ? 'cursor-move' : ''}`}>
                    <div className="flex items-center gap-3">
                        <Settings className="w-6 h-6 text-blue-300" />
                        <div>
                            <h2 className="text-xl font-bold text-white">My Settings</h2>
                            <p className="text-sm text-blue-200">
                                Role: <span className="font-semibold capitalize">{currentUser?.role || 'User'}</span>
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {/* Mobile banner */}
                    <div className="md:hidden p-3 bg-blue-900/40 border border-blue-700 rounded-lg text-sm text-blue-200">
                        Tip: for easier editing, open settings on desktop.
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-16">
                            <Loader className="w-8 h-8 text-blue-400 animate-spin" />
                        </div>
                    )}

                    {!loading && (
                        <>
                            {error && (
                                <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg flex items-center gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                                    <p className="text-red-300 text-sm">{error}</p>
                                </div>
                            )}
                            {success && (
                                <div className="p-3 bg-green-900/50 border border-green-700 rounded-lg flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                                    <p className="text-green-300 text-sm">{success}</p>
                                </div>
                            )}

                            {/* Inheritance info */}
                            <div className="p-3 bg-blue-900/30 border border-blue-700 rounded-lg flex items-start gap-3">
                                <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-blue-200">Settings Inheritance</p>
                                    <p className="text-xs text-blue-300 mt-0.5">
                                        System Defaults → Organization → Your Personal Overrides
                                    </p>
                                    <div className="mt-1.5 flex items-center gap-4 text-xs text-gray-400">
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-green-600 inline-block" />You customized</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-gray-600 inline-block" />Org default</span>
                                        <span className="flex items-center gap-1"><Lock className="w-3 h-3" />Locked</span>
                                    </div>
                                </div>
                            </div>

                            {/* Settings by Tier */}
                            {Object.entries(settingsByTier).map(([tierKey, tier]) => {
                                const isExpanded = expandedSections[tierKey];
                                return (
                                    <div key={tierKey} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
                                        <button
                                            onClick={() => toggleSection(tierKey)}
                                            className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${tier.badgeClass}`}>
                                                    {tierKey.toUpperCase()}
                                                </span>
                                                <div className="text-left">
                                                    <h3 className="font-semibold text-white text-sm">{tier.title}</h3>
                                                    <p className="text-xs text-gray-400">{tier.description}</p>
                                                </div>
                                            </div>
                                            {isExpanded
                                                ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                                        </button>

                                        {isExpanded && (
                                            <div className="border-t border-gray-700 divide-y divide-gray-700">
                                                {tier.settings.map(setting => {
                                                    const value = getEffectiveValue(setting.key);
                                                    const canEdit = canCustomize(setting.key);
                                                    const overridden = isOverridden(setting.key);
                                                    const pending = isPending(setting.key);

                                                    return (
                                                        <div key={setting.key} className={`p-4 ${
                                                            pending ? 'bg-yellow-900/20' : overridden ? 'bg-green-900/10' : ''
                                                        }`}>
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="font-medium text-white text-sm">{setting.label}</span>
                                                                        {!canEdit && <Lock className="w-3 h-3 text-gray-500" />}
                                                                        {overridden && !pending && (
                                                                            <span className="text-[10px] bg-green-900/40 border border-green-700 text-green-300 px-1.5 py-0.5 rounded">Customized</span>
                                                                        )}
                                                                        {pending && (
                                                                            <span className="text-[10px] bg-yellow-900/40 border border-yellow-700 text-yellow-300 px-1.5 py-0.5 rounded">Pending</span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-xs text-gray-500 mt-0.5">{setting.description}</p>
                                                                </div>
                                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                                    <input
                                                                        type="number"
                                                                        value={value || ''}
                                                                        onChange={(e) => handleChange(setting.key, parseFloat(e.target.value))}
                                                                        disabled={!canEdit || saving}
                                                                        min={setting.min}
                                                                        max={setting.max}
                                                                        step={setting.step || 1}
                                                                        className={`w-28 px-3 py-1.5 border rounded-lg text-right text-sm ${
                                                                            canEdit
                                                                                ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                                                                                : 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed'
                                                                        }`}
                                                                    />
                                                                    {setting.unit && (
                                                                        <span className="text-xs text-gray-400 w-10">{setting.unit}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* Sticky Footer */}
                <div className="flex-shrink-0 bg-gray-900 border-t border-gray-700 px-6 py-4 space-y-3">
                    {hasChanges && (
                        <input
                            type="text"
                            value={changeNotes}
                            onChange={(e) => setChangeNotes(e.target.value)}
                            placeholder="Why are you making these changes? (required)"
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                    )}
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={loadHistory}
                                className="flex items-center gap-1.5 px-3 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 text-sm transition-colors"
                            >
                                <Clock className="w-4 h-4" />
                                History
                            </button>
                            {Object.keys(myOverrides).length > 0 && (
                                <button
                                    onClick={() => setShowResetConfirm(true)}
                                    disabled={saving}
                                    className="flex items-center gap-1.5 px-3 py-2 border border-red-700 text-red-400 rounded-lg hover:bg-red-900/30 text-sm transition-colors"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Reset All
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 text-sm transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={!hasChanges || saving}
                                className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold transition-opacity"
                            >
                                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {saving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Reset Confirmation Modal */}
                {showResetConfirm && (
                    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
                        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-sm w-full shadow-2xl">
                            <h3 className="text-lg font-bold text-white mb-2">Reset All Settings?</h3>
                            <p className="text-gray-400 text-sm mb-5">
                                This will delete all your personal customizations and revert to organization defaults. This cannot be undone.
                            </p>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setShowResetConfirm(false)}
                                    className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReset}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold"
                                >
                                    Reset All
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* History Modal */}
                {showHistory && (
                    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
                        <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
                                <h3 className="text-lg font-bold text-white">Change History</h3>
                                <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-gray-700 rounded-lg">
                                    <X className="w-5 h-5 text-gray-400" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-6 space-y-3">
                                {history.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">No change history yet</p>
                                ) : (
                                    history.map(record => (
                                        <div key={record.id} className="p-4 bg-gray-700/50 border border-gray-600 rounded-lg">
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <Clock className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-white">
                                                        {new Date(record.changed_at).toLocaleString()}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-gray-500">
                                                    {record.changed_by_email || 'Unknown'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-300 mb-1">
                                                Changed: <span className="font-medium text-white">{record.changed_fields?.join(', ')}</span>
                                            </p>
                                            {record.change_reason && (
                                                <p className="text-xs text-gray-400 italic">"{record.change_reason}"</p>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MySettings;
